import { i18next, localized, t } from '@weavedev/lit-i18next';
import { LitElement, css, html } from 'lit';
import { customElement } from 'lit/decorators.js';

import { TYPE_OPTIONS } from '@/data';
import { BREAKPOINT_SMALL_PX } from '@/settings';
import { baseStyles } from '@/styles';
import { GTMEventType, PersonalisationType } from '@/types';
import { sendGTMEvent } from '@/utils';

/**
 * @element fg-personalisation-choose-type
 */
@customElement('fg-personalisation-choose-type')
@localized()
export class FgPersonalisationChooseType extends LitElement {
  private options = TYPE_OPTIONS;

  private chooseType(type: PersonalisationType, optionTranslationKey: string) {
    const en = i18next.getFixedT('en');
    sendGTMEvent(GTMEventType.PRODUCT_CONFIGURATOR, {
      configurator_step_name: 'print_delivery',
      configurator_step: '3',
      configurator_sub_step: '3',
      configuration_sub_step_name: 'print_selection',
      interaction_input: en(optionTranslationKey),
    });
    this.dispatchEvent(
      new CustomEvent('personalisation-choose-type', {
        detail: { type },
      }),
    );
  }

  render() {
    return html`
      ${this.options.map(
        option => html`
          <button
            @click=${() =>
              this.chooseType(option.type, option.titleTranslationKey)}
          >
            <span aria-hidden="true">${option.icon}</span>
            <div>
              <strong>${t(option.titleTranslationKey)}</strong>
              <span>${t(option.descriptionTranslationKey)}</span>
            </div>
          </button>
        `,
      )}
    `;
  }

  static styles = [
    baseStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;
        gap: 6px;
      }

      svg {
        color: var(--fg-primary, var(--fg-fallback-primary));
        flex-shrink: 0;
        height: 100%;
        margin: 5px;
        max-height: 28px;
        width: 100%;
      }

      button > span {
        display: flex;
        justify-content: center;
        align-items: center;
        width: auto;
      }

      button {
        align-items: center;
        background: white;
        border: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
        border-radius: var(--fg-borderradius, var(--fg-fallback-borderradius));
        display: flex;
        flex-basis: 100%;
        flex-grow: 1;
        justify-content: center;
        gap: 8px;
        height: 100%;
        padding: 8px;
        transition: border;
      }

      button:hover,
      button:focus {
        border-color: var(--fg-primary, var(--fg-fallback-primary));
        box-shadow: var(--fg-boxshadow, var(--fg-fallback-boxshadow));
      }

      strong {
        margin-bottom: 2px;
        font-size: 1rem;
      }

      strong,
      span {
        display: block;
      }

      @media screen and (min-width: ${BREAKPOINT_SMALL_PX}px) {
        :host {
          flex-direction: row;
        }

        button {
          aspect-ratio: 1 / 1;
          padding: 14px 8px 20px;
          flex-direction: column;
          text-align: center;
        }

        button > span {
          width: 100%;
        }
      }
    `,
  ];
}
